<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Progress Circular Component
      p.text--secondary A circular progress indicator that displays progress for determinate and indeterminate operations. Built on Vuetify's VProgressCircular component and registered as yz-progress-circular in the YDS component system.

    // Basic Examples
    .component-section.mb-8#basic-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-examples')") Basic Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in basicVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Progress Implementation
            .d-flex.justify-center.py-4
              yz-progress-circular(v-bind="variation.props")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Determinate Examples
    .component-section.mb-8#determinate-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('determinate-examples')") Determinate Progress
      .variations-grid
        .variation-item.mb-6(v-for="variation in determinateVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Progress Implementation
            .d-flex.justify-center.align-center.gap-4.flex-wrap.py-4
              div(v-for="progress in variation.progresses" :key="progress.value" class="d-flex flex-column align-center")
                yz-progress-circular(v-bind="progress.props" :value="progress.value")
                small.text--secondary.mt-2 {{ progress.value }}%

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Size Examples
    .component-section.mb-8#size-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('size-examples')") Size Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in sizeVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Progress Implementation
            .d-flex.justify-center.align-center.gap-4.flex-wrap.py-4
              div(v-for="progress in variation.progresses" :key="progress.label" class="d-flex flex-column align-center")
                yz-progress-circular(v-bind="progress.props" :value="progress.value")
                small.text--secondary.mt-2 {{ progress.label }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Color Examples
    .component-section.mb-8#color-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('color-examples')") Color Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in colorVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Progress Implementation
            .d-flex.justify-center.align-center.gap-4.flex-wrap.py-4
              div(v-for="progress in variation.progresses" :key="progress.label" class="d-flex flex-column align-center")
                yz-progress-circular(v-bind="progress.props" :value="progress.value")
                small.text--secondary.mt-2 {{ progress.label }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Width Examples
    .component-section.mb-8#width-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('width-examples')") Width Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in widthVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Progress Implementation
            .d-flex.justify-center.align-center.gap-4.flex-wrap.py-4
              div(v-for="progress in variation.progresses" :key="progress.label" class="d-flex flex-column align-center")
                yz-progress-circular(v-bind="progress.props" :value="progress.value")
                small.text--secondary.mt-2 {{ progress.label }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Rotation & Button Examples
    .component-section.mb-8#rotation-button-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('rotation-button-examples')") Rotation & Button Integration
      .variations-grid
        .variation-item.mb-6(v-for="variation in rotationVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Progress Implementation
            .d-flex.justify-center.align-center.gap-4.flex-wrap.py-4
              template(v-if="variation.isButton")
                yz-btn(v-bind="variation.buttonProps" :loading="variation.loading")
              template(v-else)
                div(v-for="progress in variation.progresses" :key="progress.label" class="d-flex flex-column align-center")
                  yz-progress-circular(v-bind="progress.props" :value="progress.value")
                  small.text--secondary.mt-2 {{ progress.label }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Custom Content Examples
    .component-section.mb-8#custom-content-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('custom-content-examples')") Custom Content
      .variations-grid
        .variation-item.mb-6(v-for="variation in customContentVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Progress Implementation
            .d-flex.justify-center.align-center.gap-4.flex-wrap.py-4
              div(v-for="progress in variation.progresses" :key="progress.label" class="d-flex flex-column align-center")
                yz-progress-circular(v-bind="progress.props" :value="progress.value")
                  template(v-if="progress.slot === 'percentage'") {{ progress.value }}%
                  template(v-if="progress.slot === 'fraction'") {{ progress.value }}/100
                  template(v-if="progress.slot === 'icon'")
                    yz-icon(size="20" color="primary") mdi-check
                small.text--secondary.mt-2 {{ progress.label }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Interactive Demo
    .component-section.mb-8#interactive-demo
      h3.mb-4.cursor-pointer(@click="navigateToSection('interactive-demo')") Interactive Demo
      .variation-item
        .variation-header.mb-3
          h4 Controllable Circular Progress
          p.text--secondary.caption Use the controls to adjust the progress value

        .variation-preview.mb-4.pa-4.border.rounded
          .d-flex.justify-center.mb-4
            yz-progress-circular(color="primary" :value="interactiveValue" :size="80" :width="6")
              | {{ interactiveValue }}%

          .d-flex.justify-center.align-center.gap-2.flex-wrap
            yz-btn(label="0%" size="sm" outlined @click="interactiveValue = 0")
            yz-btn(label="25%" size="sm" outlined @click="interactiveValue = 25")
            yz-btn(label="50%" size="sm" outlined @click="interactiveValue = 50")
            yz-btn(label="75%" size="sm" outlined @click="interactiveValue = 75")
            yz-btn(label="100%" size="sm" outlined @click="interactiveValue = 100")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === interactiveCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === interactiveCode ? 'success' : ''" @click="copyCode(interactiveCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ interactiveCode }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API

      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in componentProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      h4.mb-3 Slots
      .slots-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot
                th Description
            tbody
              tr(v-for="slot in componentSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}

      h4.mb-3 Common Use Cases
      .use-cases-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Scenario
                th Recommended Approach
                th Example
            tbody
              tr(v-for="useCase in useCases" :key="useCase.scenario")
                td.font-weight-medium.text-no-wrap {{ useCase.scenario }}
                td.text--secondary {{ useCase.approach }}
                td.single-line {{ useCase.example }}

      h4.mb-3 Implementation Notes
      .notes-section
        v-alert(type="info" outlined)
          ul.mb-0
            li
              strong Indeterminate:
              | Use for unknown duration operations like initial loads or background syncing
            li
              strong Determinate:
              | Use when you can calculate exact progress percentage
            li
              strong Size:
              | Default is 32px. Increase for more prominent displays or to show value text inside
            li
              strong Width:
              | Controls the thickness of the circular stroke. Default is 4px
            li
              strong Color:
              | Supports Vuetify color names (primary, success, error, warning, info) and hex values
            li
              strong Rotation:
              | Rotate prop allows you to change the starting position of the progress arc
            li
              strong Button Integration:
              | Can be used with yz-btn component's loading state for async operations
            li
              strong Default Slot:
              | Use to display custom content inside the circle (percentage, icons, text)
            li
              strong Accessibility:
              | Component automatically includes ARIA attributes for screen readers
            li
              strong Registration:
              | This component is Vuetify's VProgressCircular registered as yz-progress-circular in the YDS system
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      interactiveValue: 65
    }
  },
  computed: {
    basicVariations() {
      return [
        {
          name: 'Indeterminate Progress',
          id: 'indeterminate-progress',
          description: 'Loading spinner for operations with unknown duration',
          label: 'Indeterminate (Loading)',
          hint: 'Use for initial loads or background processes',
          props: {
            indeterminate: true,
            color: 'primary'
          },
          code: `yz-progress-circular(indeterminate color="primary")`
        },
        {
          name: 'Default Determinate',
          id: 'default-determinate',
          description: 'Circular progress with a specific value',
          label: 'Determinate Progress',
          hint: '70% complete',
          props: {
            value: 70,
            color: 'primary'
          },
          code: `yz-progress-circular(:value="70" color="primary")`
        }
      ]
    },
    determinateVariations() {
      return [
        {
          name: 'Progress Stages',
          id: 'progress-stages',
          description: 'Circular progress at different completion stages',
          label: 'Different Progress Values',
          hint: '0%, 25%, 50%, 75%, 100%',
          progresses: [
            { value: 0, props: { color: 'grey' } },
            { value: 25, props: { color: 'primary' } },
            { value: 50, props: { color: 'primary' } },
            { value: 75, props: { color: 'primary' } },
            { value: 100, props: { color: 'success' } }
          ],
          code: `yz-progress-circular(:value="0" color="grey")
yz-progress-circular(:value="25" color="primary")
yz-progress-circular(:value="50" color="primary")
yz-progress-circular(:value="75" color="primary")
yz-progress-circular(:value="100" color="success")`
        }
      ]
    },
    sizeVariations() {
      return [
        {
          name: 'Size Variants',
          id: 'size-variants',
          description: 'Different sizes for various UI contexts',
          label: 'Custom Sizes',
          hint: 'size prop in pixels',
          progresses: [
            { label: '16px', props: { color: 'primary', size: 16 }, value: 70 },
            { label: '24px', props: { color: 'primary', size: 24 }, value: 70 },
            { label: '32px (default)', props: { color: 'primary', size: 32 }, value: 70 },
            { label: '48px', props: { color: 'primary', size: 48 }, value: 70 },
            { label: '64px', props: { color: 'primary', size: 64 }, value: 70 }
          ],
          code: `yz-progress-circular(:value="70" color="primary" :size="16")
yz-progress-circular(:value="70" color="primary" :size="24")
yz-progress-circular(:value="70" color="primary" :size="32")
yz-progress-circular(:value="70" color="primary" :size="48")
yz-progress-circular(:value="70" color="primary" :size="64")`
        }
      ]
    },
    colorVariations() {
      return [
        {
          name: 'Semantic Colors',
          id: 'semantic-colors',
          description: 'Using Vuetify semantic color names',
          label: 'Semantic Color Variants',
          hint: 'primary, success, warning, error, info',
          progresses: [
            { label: 'Primary', props: { color: 'primary', size: 48 }, value: 60 },
            { label: 'Success', props: { color: 'success', size: 48 }, value: 80 },
            { label: 'Warning', props: { color: 'warning', size: 48 }, value: 50 },
            { label: 'Error', props: { color: 'error', size: 48 }, value: 30 },
            { label: 'Info', props: { color: 'info', size: 48 }, value: 70 }
          ],
          code: `yz-progress-circular(:value="60" color="primary" :size="48")
yz-progress-circular(:value="80" color="success" :size="48")
yz-progress-circular(:value="50" color="warning" :size="48")
yz-progress-circular(:value="30" color="error" :size="48")
yz-progress-circular(:value="70" color="info" :size="48")`
        },
        {
          name: 'Custom Hex Colors',
          id: 'custom-hex-colors',
          description: 'Using custom hex color values',
          label: 'Custom Colors',
          hint: 'Direct hex values',
          progresses: [
            { label: 'Purple', props: { color: '#8B5CF6', size: 48 }, value: 65 },
            { label: 'Pink', props: { color: '#EC4899', size: 48 }, value: 65 },
            { label: 'Teal', props: { color: '#14B8A6', size: 48 }, value: 65 }
          ],
          code: `yz-progress-circular(:value="65" color="#8B5CF6" :size="48")
yz-progress-circular(:value="65" color="#EC4899" :size="48")
yz-progress-circular(:value="65" color="#14B8A6" :size="48")`
        }
      ]
    },
    widthVariations() {
      return [
        {
          name: 'Stroke Width Variants',
          id: 'stroke-width-variants',
          description: 'Different stroke widths for the circular progress',
          label: 'Custom Widths',
          hint: 'width prop controls stroke thickness',
          progresses: [
            { label: 'Thin (2px)', props: { color: 'primary', size: 48, width: 2 }, value: 70 },
            { label: 'Default (4px)', props: { color: 'primary', size: 48, width: 4 }, value: 70 },
            { label: 'Medium (6px)', props: { color: 'primary', size: 48, width: 6 }, value: 70 },
            { label: 'Thick (8px)', props: { color: 'primary', size: 48, width: 8 }, value: 70 }
          ],
          code: `yz-progress-circular(:value="70" color="primary" :size="48" :width="2")
yz-progress-circular(:value="70" color="primary" :size="48" :width="4")
yz-progress-circular(:value="70" color="primary" :size="48" :width="6")
yz-progress-circular(:value="70" color="primary" :size="48" :width="8")`
        }
      ]
    },
    rotationVariations() {
      return [
        {
          name: 'Rotation Angles',
          id: 'rotation-angles',
          description: 'Change the starting position of the progress arc',
          label: 'Different Rotations',
          hint: 'rotate prop in degrees',
          progresses: [
            { label: '0° (default)', props: { color: 'primary', size: 48, rotate: 0 }, value: 75 },
            { label: '90°', props: { color: 'primary', size: 48, rotate: 90 }, value: 75 },
            { label: '180°', props: { color: 'primary', size: 48, rotate: 180 }, value: 75 },
            { label: '270°', props: { color: 'primary', size: 48, rotate: 270 }, value: 75 }
          ],
          code: `yz-progress-circular(:value="75" color="primary" :size="48" :rotate="0")
yz-progress-circular(:value="75" color="primary" :size="48" :rotate="90")
yz-progress-circular(:value="75" color="primary" :size="48" :rotate="180")
yz-progress-circular(:value="75" color="primary" :size="48" :rotate="270")`
        },
        {
          name: 'Button Loading State',
          id: 'button-loading',
          description: 'Progress circular integrated with button component',
          label: 'Button with Loading',
          hint: 'loading prop on yz-btn',
          isButton: true,
          loading: true,
          buttonProps: {
            label: 'Loading...',
            loading: true
          },
          code: `yz-btn(label="Loading..." loading)`
        }
      ]
    },
    customContentVariations() {
      return [
        {
          name: 'Percentage Display',
          id: 'percentage-display',
          description: 'Show percentage value inside the circle',
          label: 'With Percentage',
          hint: 'Using default slot',
          progresses: [
            { label: '25%', props: { color: 'primary', size: 64, width: 4 }, value: 25, slot: 'percentage' },
            { label: '50%', props: { color: 'primary', size: 64, width: 4 }, value: 50, slot: 'percentage' },
            { label: '75%', props: { color: 'primary', size: 64, width: 4 }, value: 75, slot: 'percentage' },
            { label: '100%', props: { color: 'success', size: 64, width: 4 }, value: 100, slot: 'percentage' }
          ],
          code: `yz-progress-circular(:value="25" color="primary" :size="64") 25%
yz-progress-circular(:value="50" color="primary" :size="64") 50%
yz-progress-circular(:value="75" color="primary" :size="64") 75%
yz-progress-circular(:value="100" color="success" :size="64") 100%`
        },
        {
          name: 'Fraction Display',
          id: 'fraction-display',
          description: 'Show fraction inside the circle',
          label: 'With Fraction',
          hint: 'Custom slot content',
          progresses: [
            { label: '25/100', props: { color: 'info', size: 64, width: 4 }, value: 25, slot: 'fraction' },
            { label: '60/100', props: { color: 'info', size: 64, width: 4 }, value: 60, slot: 'fraction' }
          ],
          code: `yz-progress-circular(:value="25" color="info" :size="64") 25/100
yz-progress-circular(:value="60" color="info" :size="64") 60/100`
        },
        {
          name: 'Icon Display',
          id: 'icon-display',
          description: 'Show icon inside the circle',
          label: 'With Icon',
          hint: 'Icon in slot',
          progresses: [
            { label: 'Complete', props: { color: 'success', size: 64, width: 4 }, value: 100, slot: 'icon' }
          ],
          code: `yz-progress-circular(:value="100" color="success" :size="64")
  yz-icon(size="20" color="primary") mdi-check`
        }
      ]
    },
    interactiveCode() {
      return `// Template
yz-progress-circular(color="primary" :value="progressValue" :size="80" :width="6")
  | {{ progressValue }}%

// Script
data() {
  return {
    progressValue: 65
  }
}`
    },
    componentProps() {
      return [
        {
          name: 'value',
          type: 'Number',
          default: '0',
          description: 'Progress value from 0 to 100. Required for determinate mode.'
        },
        {
          name: 'indeterminate',
          type: 'Boolean',
          default: 'false',
          description: 'Displays continuous spinning animation for operations with unknown duration'
        },
        {
          name: 'color',
          type: 'String',
          default: 'primary',
          description: 'Progress circle color. Supports Vuetify colors (primary, success, error, etc.) and hex values'
        },
        {
          name: 'size',
          type: 'Number | String',
          default: '32',
          description: 'Diameter of the circle in pixels'
        },
        {
          name: 'width',
          type: 'Number | String',
          default: '4',
          description: 'Stroke width of the circle in pixels'
        },
        {
          name: 'rotate',
          type: 'Number | String',
          default: '0',
          description: 'Rotation angle in degrees. Changes the starting position of the progress arc'
        },
        {
          name: 'button',
          type: 'Boolean',
          default: 'false',
          description: 'Applies button loading styles (used internally by yz-btn)'
        }
      ]
    },
    componentSlots() {
      return [
        {
          name: 'default',
          description: 'Content to display inside the circle (percentage, icons, text, etc.)'
        }
      ]
    },
    useCases() {
      return [
        {
          scenario: 'Loading state',
          approach: 'Use indeterminate mode with appropriate size',
          example: 'yz-progress-circular(indeterminate color="primary")'
        },
        {
          scenario: 'File upload',
          approach: 'Show percentage inside circle',
          example: 'yz-progress-circular(:value="uploadProgress") {{ uploadProgress }}%'
        },
        {
          scenario: 'Button loading',
          approach: 'Use loading prop on yz-btn component',
          example: 'yz-btn(label="Submit" loading)'
        },
        {
          scenario: 'Dashboard widget',
          approach: 'Large circle with custom content',
          example: 'yz-progress-circular(:value="score" :size="120") {{ score }}/100'
        },
        {
          scenario: 'Inline indicator',
          approach: 'Small indeterminate spinner',
          example: 'yz-progress-circular(indeterminate :size="16")'
        },
        {
          scenario: 'Progress visualization',
          approach: 'Thick stroke with percentage',
          example: 'yz-progress-circular(:value="percent" :width="8" :size="80")'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-2 {
  gap: 8px;
}

.gap-4 {
  gap: 16px;
}
</style>
